import React, { useState } from 'react';
import { MapPin } from '@phosphor-icons/react';
import { Link } from '../../../../components/Link';
import { Msg, useFormatMessage } from '../../../../i18n-review/client/Msg';
import { formatSalaryEuros } from '../../../../utils/salary/salary-role-seo-meta';
import type { EmployerSector } from '../../../../utils/salary/employer-sector';
import { MIN_EMPLOYER_SAMPLE } from '../../../../utils/salary/role-market-summary';
import {
  computeLanguageShares,
  computeOfficialGap,
  computeOfficialSectorGap,
  computeSectorGap,
  percentVs,
  MIN_LANGUAGE_SHARE_PCT,
} from '../../../../utils/salary/salary-role-insights';
import { Accordion } from '../../../../design-system/ui/Accordion/Accordion';
import { Button } from '../../../../design-system/ui/Button/Button';
import { Card } from '../../../../design-system/ui/Card/Card';
import { Chip } from '../../../../design-system/ui/Chip/Chip';
import { MarketBar } from '../../../../design-system/ui/MarketBar/MarketBar';
import { Table } from '../../../../design-system/ui/Table/Table';
import { TrendChip } from '../../../../design-system/ui/TrendChip/TrendChip';
import { BarChart } from '../../../../design-system/ui/charts/BarChart';
import { TemplateSection } from '../../../../design-system/ui/templates/Section';
import type { Data } from '../+data.server';
import { v2Messages as m } from './salary-role-v2.messages';
import { netMonthlyFor } from '../../../../utils/salary/net-pay-grid';

type Loc = 'en' | 'fi' | 'sv';

/** On-page euro amount; non-breaking spaces so "2 864 €" never wraps mid-number. */
export function eur(value: number, locale: Loc): string {
  return formatSalaryEuros(value, locale).replace(/ /g, ' ');
}

/** "Helsinki, Suomi, Vantaa, Suomi" -> "Helsinki, Vantaa": the country adds nothing on a Finland page. */
export function cleanLocation(location: string): string {
  const parts = location
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && !/^(suomi|finland|finland)$/i.test(p));
  return [...new Set(parts)].join(', ');
}

/** "2 811–3 071 €" in fi/sv, "€2,811–3,071" in en. */
export function eurRange(min: number, max: number, locale: Loc): string {
  if (Math.round(min) === Math.round(max)) return eur(min, locale);
  const bare = (v: number) =>
    eur(v, locale)
      .replace(/[\s ]?€$/, '')
      .replace(/^€/, '');
  return locale === 'en' ? `€${bare(min)}–${bare(max)}` : `${bare(min)}–${bare(max)} €`;
}

/** Date the page's figures refer to: the stats row's lastUpdated, so SSR and hydration agree on cached pages. */
export function figuresDate(data: Data): Date {
  const raw = data.overallStats?.lastUpdated;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d : new Date(0);
}

const SECTOR_LABEL = {
  public: m.sectorPublicShort,
  private: m.sectorPrivateShort,
  agency: m.sectorAgencyShort,
} as const;

// ── Provenance ────────────────────────────────────────────────────────────────

/** The source line under the headline figure: when, how many ads, how many of the open ads state pay. */
export function Provenance({ data, locale }: { data: Data; locale: Loc }) {
  const stats = data.overallStats;
  const market = data.market;
  const updated = stats?.lastUpdated
    ? new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale === 'sv' ? 'sv-FI' : 'fi-FI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        timeZone: 'Europe/Helsinki',
      }).format(new Date(stats.lastUpdated))
    : null;
  const sample = stats?.sampleSize ?? stats?.count ?? null;
  const statedPct =
    market && market.summary.openCount > 0
      ? Math.round((market.summary.statedSalaryCount / Math.min(market.summary.openCount, 250)) * 100)
      : null;
  const days =
    typeof stats?.medianDaysOpen === 'number' && (stats?.daysOpenSampleSize ?? 0) >= 8
      ? Math.round(stats.medianDaysOpen)
      : null;
  const items: React.ReactNode[] = [];
  if (updated) items.push(<Msg key="u" {...m.provUpdated} values={{ date: updated }} />);
  if (sample) items.push(<Msg key="p" {...m.provPostings} values={{ count: sample }} />);
  if (statedPct !== null) items.push(<Msg key="s" {...m.provStatedShare} values={{ pct: statedPct }} />);
  if (days !== null) items.push(<Msg key="d" {...m.provDaysOpen} values={{ days }} />);
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 type-caption text-jc-ink-secondary">
      {items.map((item, i) => (
        <span key={i}>{item}</span>
      ))}
    </p>
  );
}

// ── Insight tiles ─────────────────────────────────────────────────────────────

export function InsightTiles({ data, locale, medianMid }: { data: Data; locale: Loc; medianMid: number | null }) {
  const tiles: Array<{ key: string; eyebrow: React.ReactNode; headline: React.ReactNode; detail: React.ReactNode }> =
    [];
  const sectorGap = computeSectorGap(data.market?.summary);
  if (sectorGap) {
    tiles.push({
      key: 'sector',
      eyebrow: <Msg {...m.insightEmployerEyebrow} />,
      headline: (
        <Msg
          {...(sectorGap.gap > 0 ? m.insightSectorMore : m.insightSectorLess)}
          values={{ gap: eur(Math.abs(sectorGap.gap), locale) }}
        />
      ),
      detail: (
        <Msg
          {...m.insightSectorDetail}
          values={{
            publicPay: eur(sectorGap.publicPay, locale),
            privatePay: eur(sectorGap.privatePay, locale),
            publicCount: sectorGap.publicCount,
            privateCount: sectorGap.privateCount,
          }}
        />
      ),
    });
  }
  const officialGap = computeOfficialGap(data.official, medianMid, data.officialSource?.year ?? 2024);
  if (officialGap) {
    tiles.push({
      key: 'official',
      eyebrow: <Msg {...m.insightOfficialEyebrow} />,
      headline: (
        <Msg
          {...(officialGap.gap > 0 ? m.insightOfficialHigher : m.insightOfficialLower)}
          values={{ gap: eur(Math.abs(officialGap.gap), locale) }}
        />
      ),
      detail: (
        <Msg
          {...m.insightOfficialDetail}
          values={{ official: eur(officialGap.official, locale), year: officialGap.year }}
        />
      ),
    });
  }
  const openSample = Math.min(data.market?.summary.openCount ?? 0, 250);
  const langs = computeLanguageShares(data.market?.requiredLanguages, openSample);
  const swedish = langs.find((l) => l.lang === 'sv');
  const english = langs.find((l) => l.lang === 'en');
  const langHeadline =
    swedish && swedish.pct >= MIN_LANGUAGE_SHARE_PCT ? (
      <Msg {...m.insightSwedish} values={{ pct: swedish.pct }} />
    ) : english && english.pct >= MIN_LANGUAGE_SHARE_PCT ? (
      <Msg {...m.insightEnglishOnly} values={{ pct: english.pct }} />
    ) : null;
  if (langHeadline && langs.length > 0) {
    tiles.push({
      key: 'lang',
      eyebrow: <Msg {...m.insightLanguageEyebrow} />,
      headline: langHeadline,
      detail: (
        <Msg
          {...m.insightLanguageDetail}
          values={{ count: openSample, list: langs.map((l) => `${l.label} ${l.pct} %`).join(', ') }}
        />
      ),
    });
  }
  if (tiles.length === 0) return null;
  return (
    <section
      className={`grid grid-cols-1 gap-4 ${tiles.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}
      aria-label="Insights"
    >
      {tiles.map((t) => (
        <Card key={t.key} padding="sm" flat className="flex flex-col gap-2">
          <p className="type-eyebrow text-jc-ink-secondary">{t.eyebrow}</p>
          <p className="type-subheading text-jc-ink">{t.headline}</p>
          <p className="type-caption text-jc-ink-secondary">{t.detail}</p>
        </Card>
      ))}
    </section>
  );
}

// ── Shareable sector finding ──────────────────────────────────────────────────

const FINDING_ANCHOR: Record<Loc, string> = {
  fi: 'julkinen-vs-yksityinen',
  sv: 'offentlig-vs-privat',
  en: 'public-vs-private',
};

export function SectorFinding({
  data,
  locale,
  roleTitle,
  canonicalPath,
}: {
  data: Data;
  locale: Loc;
  roleTitle: string;
  canonicalPath: string;
}) {
  const formatMessage = useFormatMessage();
  const [copied, setCopied] = useState(false);
  const [showCite, setShowCite] = useState(false);
  const gap = computeSectorGap(data.market?.summary);
  if (!gap) return null;
  const officialSector = computeOfficialSectorGap(data.official);
  const anchor = FINDING_ANCHOR[locale];
  const url = `https://www.jobcrawls.com${canonicalPath}#${anchor}`;
  const now = figuresDate(data);
  const month = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale === 'sv' ? 'sv-FI' : 'fi-FI', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Helsinki',
  }).format(now);
  const date = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale === 'sv' ? 'sv-FI' : 'fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    timeZone: 'Europe/Helsinki',
  }).format(now);
  const headline = formatMessage(gap.gap > 0 ? m.insightSectorMore : m.insightSectorLess, {
    gap: eur(Math.abs(gap.gap), locale),
  });
  const rows = [
    { id: 'public', label: formatMessage(m.sectorPublicShort), pay: gap.publicPay },
    { id: 'private', label: formatMessage(m.sectorPrivateShort), pay: gap.privatePay },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (permissions, insecure context); the anchor link still works.
    }
  };
  return (
    <TemplateSection
      id={anchor}
      eyebrow={<Msg {...m.findingEyebrow} values={{ month }} />}
      title={headline}
      action={
        <a href={`#${anchor}`} className="hidden type-caption text-jc-ink-secondary hover:underline md:inline">
          jobcrawls.com{canonicalPath}#{anchor}
        </a>
      }
    >
      <BarChart
        description={`${headline}. ${rows.map((r) => `${r.label}: ${eur(r.pay, locale)}`).join(', ')}.`}
        highlightId={gap.gap > 0 ? 'public' : 'private'}
        rows={rows.map((r) => ({ id: r.id, label: r.label, value: r.pay, display: eur(r.pay, locale) }))}
      />
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.findingDetail} values={{ publicCount: gap.publicCount, privateCount: gap.privateCount }} />
        {officialSector && officialSector.gap !== 0 ? (
          <>
            {' '}
            <Msg
              {...m.findingOfficial}
              values={{
                gap: eur(Math.abs(officialSector.gap), locale),
                wellbeing: eur(officialSector.wellbeing, locale),
                privateSector: eur(officialSector.privateSector, locale),
                year: data.officialSource?.year ?? 2024,
              }}
            />
          </>
        ) : null}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={copy}>
          {copied ? formatMessage(m.findingCopied) : formatMessage(m.findingCopyLink)}
        </Button>
        <Button variant="secondary" aria-expanded={showCite} onClick={() => setShowCite((v) => !v)}>
          <Msg {...m.findingCite} />
        </Button>
      </div>
      {showCite ? (
        <p className="rounded-jc-info bg-jc-elevated p-4 text-sm text-jc-ink">
          <Msg {...m.findingCiteText} values={{ year: now.getFullYear(), roleTitle, date, url }} />
        </p>
      ) : null}
    </TemplateSection>
  );
}

// ── Per-employer pay (open ads) ───────────────────────────────────────────────

export function EmployerPayChart({ data, locale }: { data: Data; locale: Loc; medianMid: number | null }) {
  const formatMessage = useFormatMessage();
  const employers = data.market?.summary.employers ?? [];
  if (employers.length < 2) return null;
  const sorted = [...employers].sort((a, b) => b.median - a.median);
  return (
    <TemplateSection
      id="employers"
      title={<Msg {...m.employersTitle} />}
      description={<Msg {...m.employersSubTable} />}
    >
      <Table
        caption={formatMessage(m.employersTableCaption)}
        rows={sorted}
        rowKey={(e) => e.companyId}
        columns={[
          {
            key: 'employer',
            label: formatMessage(m.employersColEmployer),
            render: (e) => <span title={e.name}>{e.name}</span>,
          },
          {
            key: 'sector',
            label: formatMessage(m.employersColSector),
            render: (e) => <Chip>{formatMessage(SECTOR_LABEL[e.sector as EmployerSector])}</Chip>,
          },
          { key: 'ads', label: formatMessage(m.employersColAds), numeric: true, render: (e) => e.n },
          {
            key: 'median',
            label: formatMessage(m.employersColMedian),
            numeric: true,
            render: (e) => eur(e.median, locale),
          },
        ]}
      />
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.employersFootnote} values={{ min: MIN_EMPLOYER_SAMPLE }} />
      </p>
    </TemplateSection>
  );
}

// ── Advertised vs official earnings ───────────────────────────────────────────

export function OfficialEarningsPanel({
  data,
  locale,
  medianMid,
}: {
  data: Data;
  locale: Loc;
  medianMid: number | null;
}) {
  const formatMessage = useFormatMessage();
  const official = data.official;
  const total = official?.total;
  // With no ads median (official-figure roles) the panel is the page's main figure: the ads row
  // drops out and the official bar stands alone.
  if (!official || !total) return null;
  const year = data.officialSource?.year ?? 2024;
  const sectorRows = [
    { key: 'w', label: m.officialWellbeing, s: official.wellbeingAreas },
    { key: 'p', label: m.officialPrivate, s: official.privateSector },
    { key: 'l', label: m.officialLocal, s: official.localGovernment },
    { key: 's', label: m.officialState, s: official.state },
  ].filter((r) => r.s && r.s.count >= 100);
  const label = official.labels[locale] ?? official.labels.fi;
  const countFmt = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fi-FI').format(total.count).replace(/[  ]/g, ' ');
  // One scale for both bars, so the gap between ads and earnings is the gap on screen.
  const values = [
    ...(medianMid ? [medianMid] : []),
    total.regularMedian,
    total.regularP10 ?? total.regularMedian,
    total.regularP90 ?? total.regularMedian,
  ];
  const lo = Math.floor((Math.min(...values) * 0.9) / 100) * 100;
  const hi = Math.ceil((Math.max(...values) * 1.08) / 100) * 100;
  const hasBand = Boolean(total.regularP10 && total.regularP90);
  return (
    <TemplateSection id="official" title={<Msg {...m.officialTitle} />}>
      <div className="flex flex-col gap-5">
        {medianMid ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-jc-ink">
                <Msg {...m.officialAdsRow} />
              </span>
              <span className="type-figure-sm text-jc-ink">{eur(medianMid, locale)}</span>
            </div>
            <MarketBar
              variant="compact"
              barSize="md"
              className="w-full!"
              scale={{ min: lo, max: hi }}
              median={medianMid}
              description={formatMessage(m.officialBarDesc, {
                ads: eur(medianMid, locale),
                official: eur(total.regularMedian, locale),
              })}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-jc-ink">
              <Msg {...m.officialEarningsRow} />
            </span>
            <span className="type-figure-sm text-jc-ink">{eur(total.regularMedian, locale)}</span>
          </div>
          <MarketBar
            variant="compact"
            barSize="md"
            className="w-full!"
            scale={{ min: lo, max: hi }}
            median={total.regularMedian}
            p25={hasBand ? total.regularP10! : undefined}
            p75={hasBand ? total.regularP90! : undefined}
            description={
              medianMid
                ? formatMessage(m.officialBarDesc, {
                    ads: eur(medianMid, locale),
                    official: eur(total.regularMedian, locale),
                  })
                : eur(total.regularMedian, locale)
            }
          />
          {hasBand ? (
            <p className="type-caption text-jc-ink-secondary">
              <Msg
                {...m.officialBand}
                values={{ p10: eur(total.regularP10!, locale), p90: eur(total.regularP90!, locale) }}
              />
            </p>
          ) : null}
        </div>
      </div>
      <p className="text-sm text-jc-ink-secondary">
        <Msg {...m.officialText} values={{ label, count: countFmt, official: eur(total.regularMedian, locale) }} />
      </p>
      {sectorRows.length >= 2 ? (
        <div className="flex flex-col gap-2">
          <h3 className="type-eyebrow text-jc-ink-secondary">
            <Msg {...m.officialSectorsTitle} />
          </h3>
          <ul className="m-0 grid list-none grid-cols-1 gap-x-8 p-0 sm:grid-cols-2">
            {sectorRows.map((r) => (
              <li
                key={r.key}
                className="flex items-baseline justify-between gap-2 border-b border-jc-border py-2 text-sm"
              >
                <span className="text-jc-ink">
                  <Msg {...r.label} />
                </span>
                <span className="font-semibold tabular-nums text-jc-ink">{eur(r.s!.regularMedian, locale)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.officialSource} values={{ year }} />{' '}
        <a
          href={data.officialSource?.url ?? 'https://stat.fi'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-jc-ink underline underline-offset-2"
        >
          stat.fi ↗
        </a>
      </p>
    </TemplateSection>
  );
}

// ── Role intro ────────────────────────────────────────────────────────────────

/** One link per source site ("kt.fi", "eduskunta.fi"), to the first page cited from it. */
function sourceSites(urls: string[]): Array<{ host: string; url: string }> {
  const seen = new Map<string, string>();
  for (const url of urls) {
    const host = new URL(url).hostname.replace(/^www\d*\./, '');
    if (!seen.has(host)) seen.set(host, url);
  }
  return [...seen].map(([host, url]) => ({ host, url }));
}

/**
 * Hand-researched intro for the official-figure roles (role-intros.ts): who employs them, how pay
 * is set, what training the job needs. Those pages have few or no ads, so without it they are a
 * figure and a table; the sources are listed so a reader can check every claim.
 */
export function RoleIntro({ data }: { data: Data }) {
  const intro = data.roleIntro;
  if (!intro) return null;
  return (
    <TemplateSection id="tyo-ja-koulutus" title={<Msg {...m.introTitle} />}>
      <p className="text-jc-ink">{intro.text}</p>
      {intro.sources.length > 0 ? (
        <p className="type-caption text-jc-ink-secondary">
          <Msg {...m.introSources} />{' '}
          {sourceSites(intro.sources).map(({ host, url }, i) => (
            <React.Fragment key={host}>
              {i > 0 ? ', ' : null}
              <a href={url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                {host}
              </a>
            </React.Fragment>
          ))}
        </p>
      ) : null}
    </TemplateSection>
  );
}

// ── What the job involves ─────────────────────────────────────────────────────

/** ESCO's description of the occupation ("Mitä sairaanhoitaja tekee?"), with the required credit. */
export function OccupationAbout({ data, roleNom }: { data: Data; roleNom: string }) {
  const about = data.occupationDescription;
  if (!about) return null;
  return (
    <TemplateSection id="tehtavat" title={<Msg {...m.aboutTitle} values={{ roleTitle: roleNom, roleNom }} />}>
      <p className="text-jc-ink">{about.text}</p>
      <p className="type-caption text-jc-ink-secondary">
        <a href={about.uri} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
          <Msg {...m.aboutSource} values={{ label: about.label }} />
        </a>
      </p>
    </TemplateSection>
  );
}

// ── Collective agreement minimum ──────────────────────────────────────────────

/** The values tesText takes, shared by the section and the FAQ answer so both say the same. */
export function tesTextValues(tes: NonNullable<Data['tes']>, locale: Loc): Record<string, string> {
  const hourly =
    tes.hourly != null
      ? new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(tes.hourly)
      : null;
  const [y, mo, d] = tes.validFrom.split('-').map(Number);
  return {
    agreement: tes.agreementName[locale],
    hasMonthly: tes.monthly != null ? 'yes' : 'no',
    monthly: tes.monthly != null ? eur(tes.monthly, locale) : '',
    hasHourly: hourly ? 'yes' : 'no',
    hourly: hourly ? (locale === 'en' ? `€${hourly}` : `${hourly}\u00a0€`) : '',
    label: tes.label[locale],
    validFrom: locale === 'en' ? `${d}/${mo}/${y}` : `${d}.${mo}.${y}`,
  };
}

/**
 * The collective agreement's entry-level table pay for the role: the "TES palkka" searches
 * ("kaupan alan tes palkka" 3 600/mo) and the floor every ad and statistic sits above.
 */
export function TesMinimumSection({ data, locale }: { data: Data; locale: Loc }) {
  const list = data.tesList ?? [];
  if (list.length === 0) return null;
  return (
    <TemplateSection id="tes" title={<Msg {...m.tesTitle} />}>
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {list.map((tes) => (
          <li key={tes.agreementId} className="flex flex-col gap-1">
            <p className="text-jc-ink">
              <Msg {...m.tesText} values={tesTextValues(tes, locale)} />
            </p>
            {!tes.generallyBinding ? (
              <p className="type-caption text-jc-ink-secondary">
                <Msg {...m.tesNotBinding} />
              </p>
            ) : null}
            {tes.href ? (
              <a
                href={tes.href}
                {...(tes.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="text-sm font-semibold text-jc-ink underline underline-offset-4"
              >
                <Msg {...m.tesLink} /> {tes.external ? '↗' : '→'}
              </a>
            ) : null}
          </li>
        ))}
      </ul>
    </TemplateSection>
  );
}

// ── Take-home table ───────────────────────────────────────────────────────────

export interface TakeHomeRow {
  label: React.ReactNode;
  gross: number;
}

/**
 * Gross and take-home at three points of the role's pay: the low, middle and high of whichever
 * figure leads the page (Statistics Finland's P10/median/P90, or the ads' P25/median/P75). The
 * "paljonko jää käteen" question follows every salary search; one net figure answers it only for
 * the median earner.
 */
export function TakeHomeTable({ rows, source, locale }: { rows: TakeHomeRow[]; source: 'official' | 'ads'; locale: Loc }) {
  if (rows.length === 0) return null;
  return (
    <TemplateSection id="kateen" title={<Msg {...m.takeHomeTitle} />}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-jc-border text-left text-jc-ink-secondary">
            <th scope="col" className="py-2 pr-4 font-semibold" />
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              <Msg {...m.takeHomeGross} />
            </th>
            <th scope="col" className="py-2 text-right font-semibold">
              <Msg {...m.takeHomeNet} />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-jc-border">
              <th scope="row" className="py-2 pr-4 text-left font-normal text-jc-ink">
                {row.label}
              </th>
              <td className="py-2 pr-4 text-right tabular-nums text-jc-ink">{eur(row.gross, locale)}</td>
              <td className="py-2 text-right font-semibold tabular-nums text-jc-ink">
                {eur(netMonthlyFor(row.gross), locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.takeHomeNote} values={{ source }} />
      </p>
    </TemplateSection>
  );
}

// ── Seniority table ───────────────────────────────────────────────────────────

const LEVEL_MESSAGE: Record<string, typeof m.levelMid> = {
  Intern: m.levelIntern,
  Junior: m.levelJunior,
  'Mid-Level': m.levelMid,
  Senior: m.levelSenior,
  Expert: m.levelExpert,
  Manager: m.levelManager,
  Director: m.levelDirector,
  Executive: m.levelExecutive,
};

export function levelMessage(level: string) {
  return LEVEL_MESSAGE[level] ?? null;
}

/** Pay by career level, in career order (the data is a ladder, so the order is rank, not value). Levels under 10 ads are labelled rough. */
export function RoleLevelLadder({ data, locale }: { data: Data; locale: Loc }) {
  const formatMessage = useFormatMessage();
  const levels = data.roleLevels ?? [];
  if (levels.length < 2) return null;
  return (
    <TemplateSection id="levels" title={<Msg {...m.levelsTitle} />} description={<Msg {...m.levelsSub} />}>
      <Table
        caption={formatMessage(m.levelsTableCaption)}
        rows={levels}
        rowKey={(l) => l.level}
        columns={[
          {
            key: 'level',
            label: formatMessage(m.levelsColLevel),
            render: (l) => {
              const msg = levelMessage(l.level);
              return msg ? <Msg {...msg} /> : l.level;
            },
          },
          { key: 'ads', label: formatMessage(m.levelsColAds), numeric: true, render: (l) => l.n },
          {
            key: 'median',
            label: formatMessage(m.levelsColMedian),
            numeric: true,
            render: (l) => (
              <span className="inline-flex items-center justify-end gap-2">
                {l.n < 10 ? <Chip>{formatMessage(m.thinRough)}</Chip> : null}
                {eur(l.median, locale)}
              </span>
            ),
          },
        ]}
      />
      {levels.some((l) => l.n < 10) ? (
        <p className="type-caption text-jc-ink-secondary">
          <Msg {...m.levelsSmallRough} />
        </p>
      ) : null}
    </TemplateSection>
  );
}

// ── What the ads ask for and offer ────────────────────────────────────────────

function ChipList({ children }: { children: React.ReactNode }) {
  return <ul className="m-0 flex list-none flex-wrap gap-2 p-0">{children}</ul>;
}

export function AdsAskOffer({ data }: { data: Data }) {
  const market = data.market;
  if (!market) return null;
  const sample = Math.min(market.summary.openCount, 250);
  const langs = computeLanguageShares(market.requiredLanguages, sample);
  const hasJobTypes = market.jobTypes.reduce((s, j) => s + j.count, 0) > 0;
  const hasAny = market.skills.length > 0 || langs.length > 0 || market.benefits.length > 0 || hasJobTypes;
  if (!hasAny || sample < 5) return null;
  const group = (title: React.ReactNode, items: Array<{ key: string; label: React.ReactNode }>) => (
    <div className="flex flex-col gap-2">
      <h3 className="type-eyebrow text-jc-ink-secondary">{title}</h3>
      <ChipList>
        {items.map((i) => (
          <li key={i.key}>
            <Chip>{i.label}</Chip>
          </li>
        ))}
      </ChipList>
    </div>
  );
  return (
    <TemplateSection id="ask" title={<Msg {...m.askTitle} />}>
      {market.skills.length > 0
        ? group(
            <Msg {...m.askSkills} />,
            market.skills.slice(0, 10).map((s) => ({
              key: s.value,
              label: (
                <>
                  {s.value} <span className="tabular-nums opacity-75">{s.count}</span>
                </>
              ),
            })),
          )
        : null}
      {langs.length > 0
        ? group(
            <Msg {...m.askLanguages} />,
            langs.map((l) => ({ key: l.lang, label: `${l.label} ${l.pct} %` })),
          )
        : null}
      {market.benefits.length > 0
        ? group(
            <Msg {...m.askBenefits} />,
            market.benefits.slice(0, 8).map((b) => ({
              key: b.value,
              label: (
                <>
                  {b.value} <span className="tabular-nums opacity-75">{b.count}</span>
                </>
              ),
            })),
          )
        : null}
      {hasJobTypes
        ? group(
            <Msg {...m.askContract} />,
            market.jobTypes.map((j) => ({
              key: j.value,
              label: (
                <>
                  {j.value} <span className="tabular-nums opacity-75">{j.count}</span>
                </>
              ),
            })),
          )
        : null}
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.askFootnote} values={{ count: sample }} />
      </p>
    </TemplateSection>
  );
}

// ── Best-paying open jobs ─────────────────────────────────────────────────────

export function TopPayingJobs({
  data,
  locale,
  medianMid,
  jobsHref,
}: {
  data: Data;
  locale: Loc;
  medianMid: number | null;
  jobsHref: string | null;
}) {
  const jobs = data.market?.topPaying ?? [];
  if (jobs.length === 0) return null;
  return (
    <TemplateSection
      id="top-paying"
      title={<Msg {...m.topJobsTitle} />}
      description={medianMid ? <Msg {...m.topJobsSub} values={{ median: eur(medianMid, locale) }} /> : undefined}
      action={
        jobsHref && data.market ? (
          <Button href={jobsHref} variant="secondary">
            <Msg {...m.topJobsAll} values={{ count: data.market.summary.openCount }} />
          </Button>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {jobs.map((j) => {
          const top = j.salaryMax ?? j.salaryMin ?? 0;
          const pct = medianMid && top ? percentVs(top, medianMid) : 0;
          const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
          const pay = j.salaryMin && j.salaryMax ? eurRange(j.salaryMin, j.salaryMax, locale) : eur(top, locale);
          return (
            <Card key={j.href} as="article" interactive padding="sm" className="flex flex-col gap-2">
              <span className="truncate type-caption text-jc-ink-secondary">{j.company}</span>
              <Link
                href={j.href}
                className="line-clamp-2 type-subheading text-jc-ink no-underline after:absolute after:inset-0"
              >
                {j.title}
              </Link>
              {j.location ? (
                <span className="flex min-w-0 items-center gap-1 type-caption text-jc-ink-secondary">
                  <MapPin className="size-3.5 shrink-0" weight="fill" aria-hidden />
                  <span className="truncate">{cleanLocation(j.location)}</span>
                </span>
              ) : null}
              <span className="mt-auto pt-2 type-figure text-jc-ink">{pay}</span>
              {medianMid ? (
                <span>
                  <TrendChip direction={dir} tone={dir === 'up' ? 'positive' : 'neutral'}>
                    <Msg {...m.vsMedian} values={{ dir, pct: Math.abs(pct) }} />
                  </TrendChip>
                </span>
              ) : null}
            </Card>
          );
        })}
      </div>
    </TemplateSection>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────

export interface FaqItem {
  q: React.ReactNode;
  a: React.ReactNode;
}

export function SalaryFaq({ items, title }: { items: FaqItem[]; title: React.ReactNode }) {
  if (items.length === 0) return null;
  return (
    <TemplateSection id="faq" title={title}>
      <Accordion
        headingLevel={3}
        items={items.map((item, i) => ({ id: `faq-${i}`, title: item.q, content: item.a, defaultOpen: i === 0 }))}
      />
    </TemplateSection>
  );
}
